
import React, { useState } from 'react';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const CLEANUP_OPTIONS = [
  { value: 'day', label: 'Older than 1 day' },
  { value: 'week', label: 'Older than 1 week' },
  { value: 'month', label: 'Older than 1 month' },
  { value: 'quarter', label: 'Older than 3 months' },
  { value: 'halfYear', label: 'Older than 6 months' },
  { value: 'year', label: 'Older than 1 year' },
  { value: 'all', label: 'All data' },
];

interface DataManagementCardProps {
  onCleanupData: (timeframe: string) => Promise<void>;
  cleanupInProgress: boolean;
}

const DataManagementCard = ({ onCleanupData, cleanupInProgress }: DataManagementCardProps) => {
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleanupTimeframe, setCleanupTimeframe] = useState('halfYear');
  
  const handleDataCleanup = async () => {
    try {
      await onCleanupData(cleanupTimeframe);
      setShowCleanupConfirm(false);
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to clean up data');
    }
  };

  const getCleanupDescription = () => {
    const option = CLEANUP_OPTIONS.find(opt => opt.value === cleanupTimeframe);
    return option ? option.label : 'Clean up old data';
  };

  return (
    <Card className="mt-12">
      <CardHeader>
        <CardTitle>Data Management</CardTitle>
        <CardDescription>
          Manage analytics data retention and cleanup options
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showCleanupConfirm ? (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Confirm Data Cleanup</AlertTitle>
            <AlertDescription>
              <p>This will permanently delete analytics data <strong>{getCleanupDescription().toLowerCase()}</strong>. This action cannot be undone.</p>
              <div className="mt-4 flex gap-4">
                <Button 
                  variant="destructive" 
                  onClick={handleDataCleanup} 
                  disabled={cleanupInProgress}
                >
                  {cleanupInProgress ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Confirm Cleanup
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCleanupConfirm(false)}
                  disabled={cleanupInProgress}
                >
                  Cancel
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-full sm:w-64">
                <Select 
                  value={cleanupTimeframe} 
                  onValueChange={setCleanupTimeframe}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select cleanup option" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLEANUP_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                className="flex-shrink-0"
                onClick={() => setShowCleanupConfirm(true)}
                disabled={cleanupInProgress}
              >
                {cleanupInProgress ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Clean Up Data
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Data older than 6 months will be automatically purged. Manual cleanup can be triggered as needed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataManagementCard;
